import { useState, useEffect } from 'react';
import { useGameMode } from '@/contexts/GameModeContext';
import { useDebugDate } from '@/contexts/DebugDateContext';
import { WordSelectionStrategyFactory } from '@/lib/wordSelection/StrategyFactory';

/**
 * Simplified word sequence hook with strict mode isolation
 * No complex caching, no cross-mode contamination
 */
export const useSimplifiedWordSequence = () => {
  const { currentMode } = useGameMode();
  const { debugDate } = useDebugDate();
  const [wordSequence, setWordSequence] = useState<string[]>([]);
  const [playAgainTrigger, setPlayAgainTrigger] = useState(0);

  const generateSequence = (mode: string): string[] => {
    try {
      console.log(`[useSimplifiedWordSequence] Generating sequence for mode: ${mode}`, {
        debugDate: debugDate?.toISOString(),
        timestamp: new Date().toISOString()
      });

      const strategy = WordSelectionStrategyFactory.getStrategy(mode);
      const effectiveDate = debugDate || new Date();
      const sequence = strategy.getWordSequence(effectiveDate);

      console.log(`[useSimplifiedWordSequence] Generated sequence:`, {
        mode,
        sequence,
        strategyId: strategy.getStrategyId(),
        timestamp: new Date().toISOString()
      });

      return sequence;
    } catch (error) {
      console.error('[useSimplifiedWordSequence] Error generating sequence:', error);
      return ['cat', 'cats', 'caste', 'castle']; // Fallback
    }
  };


  // Check for play again flag and trigger refresh
  useEffect(() => {
    const checkPlayAgainFlag = () => {
      const playAgainFlag = localStorage.getItem('babblegram_practice_mode_play_again');
      if (playAgainFlag === 'true') {
        console.log('[useSimplifiedWordSequence] Play again flag detected, triggering refresh (flag will be cleared by strategy/initializer)');
        setPlayAgainTrigger(prev => prev + 1);
      }
    };

    // Check immediately
    checkPlayAgainFlag();

    // Also check periodically while component is mounted
    const interval = setInterval(checkPlayAgainFlag, 100);

    return () => clearInterval(interval);
  }, []);

  // Respond immediately to explicit reset events to avoid races with flag clearing
  useEffect(() => {
    const handleGameReset = () => {
      console.log('[useSimplifiedWordSequence] Game reset event detected -> refreshing sequence');
      setPlayAgainTrigger(prev => prev + 1);
    };
    window.addEventListener('babblegram_game_reset', handleGameReset);
    return () => window.removeEventListener('babblegram_game_reset', handleGameReset);
  }, []);

  // Load sequence when mode, debug date, or play again trigger changes
  useEffect(() => {
    console.log(`[useSimplifiedWordSequence] Effect triggered - mode: ${currentMode}, debugDate: ${debugDate?.toISOString()}, trigger: ${playAgainTrigger}`);
    console.log('[useSimplifiedWordSequence] Current localStorage flags:', {
      playAgainFlag: localStorage.getItem('babblegram_practice_mode_play_again'),
      forceReset: localStorage.getItem('babblegram_practice_mode_force_reset'),
      useShared: localStorage.getItem('babblegram_practice_mode_use_shared_sequence')
    });
    
    const sequence = generateSequence(currentMode);
    console.log('[useSimplifiedWordSequence] Generated sequence in effect:', sequence);
    setWordSequence(sequence);
  }, [currentMode, debugDate, playAgainTrigger]);


  return {
    wordSequence
  };
};